import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Bell, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const CustomAlertBuilder = ({ reducedMotion }) => {
  const [alerts, setAlerts] = useState([
    { id: 1, name: 'High Error Rate', condition: 'error_rate > 0.05', action: 'Slack', active: true },
    { id: 2, name: 'Low Answer Rate', condition: 'answer_rate < 0.50', action: 'Slack', active: true }
  ]);
  const [newAlert, setNewAlert] = useState({ name: '', condition: '', action: 'Slack' });

  const handleAddAlert = () => {
    if (!newAlert.name || !newAlert.condition) {
      toast.error('Please fill in all alert fields');
      return;
    }
    const alertToAdd = { ...newAlert, id: Date.now(), active: true };
    setAlerts([...alerts, alertToAdd]);
    setNewAlert({ name: '', condition: '', action: 'Email' });
    toast.success('Alert created successfully');
  };

  const handleDeleteAlert = (id) => {
    setAlerts(alerts.filter(a => a.id !== id));
    toast.success('Alert deleted');
  };

  const handleToggleAlert = (id) => {
    setAlerts(alerts.map(a => a.id === id ? { ...a, active: !a.active } : a));
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="custom-alert-builder-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Bell className="w-5 h-5 text-orange-400" />
          Custom Alert Builder
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Create rule-based alerts for critical metrics
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* New Alert Form */}
        <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 mb-6">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Create New Alert</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Alert Name"
              value={newAlert.name}
              onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500"
              data-testid="alert-name-input"
            />
            <input
              type="text"
              placeholder="Condition (e.g., error_rate > 0.1)"
              value={newAlert.condition}
              onChange={(e) => setNewAlert({ ...newAlert, condition: e.target.value })}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500"
              data-testid="alert-condition-input"
            />
            <select
              value={newAlert.action}
              onChange={(e) => setNewAlert({ ...newAlert, action: e.target.value })}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
              data-testid="alert-action-select"
            >
              {/* Phase Audit Fix B (T-1): Email channel hidden until a real
                  email provider is wired (see services/alerts/email_adapter.py
                  — currently console-only). Re-enable once SendGrid/SES/SMTP
                  is configured + smoke-tested. */}
              <option value="Slack">Slack</option>
              <option value="Webhook">Webhook</option>
            </select>
          </div>
          <Button
            onClick={handleAddAlert}
            className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="add-alert-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Alert
          </Button>
        </div>

        {/* Existing Alerts */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-300">Active Alerts ({alerts.filter(a => a.active).length})</h4>
          {alerts.map((alert, index) => (
            <div
              key={alert.id}
              className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
              style={{
                animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
              }}
              data-testid={`alert-item-${alert.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h5 className="font-medium text-slate-100" data-testid="alert-name">{alert.name}</h5>
                    <Badge
                      className={alert.active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}
                      data-testid={`alert-status-${alert.id}`}
                    >
                      {alert.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-400 mb-1" data-testid="alert-condition">Condition: <code className="px-2 py-0.5 bg-slate-900/50 rounded text-gold">{alert.condition}</code></p>
                  <p className="text-xs text-slate-500">Action: {alert.action}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleAlert(alert.id)}
                    className="text-slate-400 hover:text-slate-100"
                    data-testid={`toggle-alert-${alert.id}`}
                  >
                    {alert.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-alert-${alert.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomAlertBuilder;